/**
 * ProfilePage/ProfilePage.jsx
 * ─────────────────────────────────────────────────────────────────────────────
 * Root component for the user's profile section.
 *
 * RESPONSIBILITIES:
 *   - Renders the 4-tab navigation (Messages, Selling, Referrals, Settings)
 *   - Owns top-level inbox badge counts (unread messages, likes, followers)
 *   - Loads the user's ads and stats on mount
 *   - Passes shared props down to each tab component
 *
 * TAB COMPONENTS (each in their own subfolder):
 *   messages/  → MessagesTab  — conversations, statuses, channels
 *   selling/   → SellingTab   — user's active/sold listings
 *   referrals/ → ReferralsTab — referral links and commissions
 *   settings/  → SettingsTab  — profile image, account details, logout
 *
 * IMPORTS NOTE:
 *   authStorage utils (getStoredEmail, shouldFetchBadge) are used here to
 *   prevent duplicate API bursts when Header and ProfilePage both fire on
 *   the same window-focus event. See utils/authStorage.js for details.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { getStoredEmail, shouldFetchBadge } from '../../utils/authStorage';
import { useAuthContext } from '../../context/AuthContext';
import { useApi } from '../../hooks/useApi';
import styles from './ProfilePage.module.css';

// ── Tab components (each lives in its own subfolder) ─────────────────────────
import MessagesTab  from './messages/MessagesTab';
import SellingTab   from './selling/SellingTab';
import ReferralsTab from './referrals/ReferralsTab';
import SettingsTab  from './settings/SettingsTab';

// ── API base URL — falls back to local dev server ────────────────────────────
const API_BASE = process.env.REACT_APP_API_URL || 'https://imselling-marketplace-production.up.railway.app/api';

// ═════════════════════════════════════════════════════════════════════════════
// PROFILE PAGE COMPONENT
// ═════════════════════════════════════════════════════════════════════════════
const ProfilePage = () => {
  // ── Auth context ──────────────────────────────────────────────────────────
  const { user, profileImage, updateProfileImage, logout } = useAuthContext();
  // fetchAds removed — replaced with direct ?seller=me API call in loadUserData

  // ── Read active tab from URL on mount (?profile_tab=selling etc.) ─────────
  // Allows deep-linking directly to a specific tab, e.g. from a notification.
  const getInitialTab = () => {
    const valid = ['messages', 'selling', 'referrals', 'settings'];
    const param = new URLSearchParams(window.location.search).get('profile_tab');
    return valid.includes(param) ? param : 'messages';
  };

  // ── State ─────────────────────────────────────────────────────────────────
  const [activeTab,    setActiveTab]    = useState(getInitialTab);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl,   setPreviewUrl]   = useState(null);
  const [userProducts, setUserProducts] = useState([]);
  const [loading,      setLoading]      = useState(true);

  // Stats displayed on the selling tab and header strip
  const [stats, setStats] = useState({
    activeListings: 0,
    totalLikes:     0,
    followers:      0,
    rating:         0,
    reviewCount:    0,
  });

  // ── Inbox / notification state ────────────────────────────────────────────
  // These drive the badge on the Messages tab button.
  const [messages,          setMessages]          = useState([]);
  const [notifications,     setNotifications]     = useState([]);
  const [updates,           setUpdates]           = useState([]);
  const [unreadMsgCount,    setUnreadMsgCount]    = useState(0);
  const [newLikesCount,     setNewLikesCount]     = useState(0);
  const [newFollowersCount, setNewFollowersCount] = useState(0);

  // ── fetchInboxCounts ──────────────────────────────────────────────────────
  // Fetches all badge-relevant data in a single Promise.all burst.
  // Uses shouldFetchBadge() to debounce — if Header already fetched within
  // the last 5 seconds (e.g. same window-focus event), this skips to avoid
  // the duplicate request burst seen in the server logs.
  const fetchInboxCounts = useCallback(async () => {
    const token = localStorage.getItem('authToken') || localStorage.getItem('token');
    if (!token) return;

    // ✅ Shared 5s debounce with Header.jsx — prevents double-burst
    if (!shouldFetchBadge()) return;

    try {
      const [
        madeRes, receivedRes, notifRes,
        convoRes, likesRes, followersRes, sysMsgRes,
      ] = await Promise.all([
        fetch(`${API_BASE}/offers/made/`,            { headers: { Authorization: `Token ${token}` } }),
        fetch(`${API_BASE}/offers/received/`,        { headers: { Authorization: `Token ${token}` } }),
        fetch(`${API_BASE}/notifications/`,          { headers: { Authorization: `Token ${token}` } }),
        fetch(`${API_BASE}/messages/conversations/`, { headers: { Authorization: `Token ${token}` } }),
        fetch(`${API_BASE}/my/likes/`,               { headers: { Authorization: `Token ${token}` } }),
        fetch(`${API_BASE}/my/followers/`,           { headers: { Authorization: `Token ${token}` } }),
        fetch(`${API_BASE}/auth/system-messages/`,   { headers: { Authorization: `Token ${token}` } }),
      ]);

      const made          = madeRes.ok      ? await madeRes.json()      : [];
      const received      = receivedRes.ok  ? await receivedRes.json()  : [];
      const notifData     = notifRes.ok     ? await notifRes.json()     : { unread_count: 0, notifications: [] };
      const convoData     = convoRes.ok     ? await convoRes.json()     : { conversations: [] };
      const likesData     = likesRes.ok     ? await likesRes.json()     : { likes: [] };
      const followersData = followersRes.ok ? await followersRes.json() : { followers: [] };
      const sysMsgData    = sysMsgRes.ok    ? await sysMsgRes.json()    : { unread_count: 0 };

      // ── Count unread offer threads ────────────────────────────────────────
      const allConvos  = [...made, ...received];
      const userEmail  = user?.identifier || user?.email;
      const unreadOffers = allConvos.filter(o => {
        const isBuyer = o.buyer?.email === userEmail;
        return isBuyer ? (o.unread_by_buyer > 0) : !o.is_read;
      }).length;

      // ── Count unread direct message threads ───────────────────────────────
      const conversations = convoData.conversations || [];
      const unreadDMs = conversations.filter(c => {
        const isBuyer = (c.buyer?.email || '').toLowerCase() === (userEmail || '').toLowerCase();
        return isBuyer ? (c.unread_by_buyer > 0) : (c.unread_by_seller > 0);
      }).length;

      // ── New likes / followers since user last opened those panels ─────────
      // seenLikesCount / seenFollowersCount are watermarks set by MessagesTab
      // when the user opens the Likes or Followers panel — so the badge
      // auto-clears once the user actually views those notifications.
      const likes     = likesData.likes        || [];
      const followers = followersData.followers || [];
      const seenLikes     = parseInt(localStorage.getItem('seenLikesCount')     || '0', 10);
      const seenFollowers = parseInt(localStorage.getItem('seenFollowersCount') || '0', 10);

      // ── I'm Selling system messages unread count ──────────────────────────
      const imSellingUnread = sysMsgData.unread_count || 0;

      // ── Update state ──────────────────────────────────────────────────────
      setUnreadMsgCount(unreadOffers + unreadDMs + imSellingUnread);
      setNewLikesCount(Math.max(0, likes.length     - seenLikes));
      setNewFollowersCount(Math.max(0, followers.length - seenFollowers));
      setMessages(allConvos);
      setNotifications(notifData.notifications || []);
    } catch (e) {
      console.error('Error fetching inbox counts:', e);
    }
  }, [getStoredEmail()]);

  // ── StrictMode double-fire guard ──────────────────────────────────────────
  // React StrictMode in development mounts → unmounts → remounts every
  // component, causing useEffect to fire twice. This ref ensures the
  // initial data load only happens once per user session, not twice.
  const hasFetchedRef = useRef(false);

  // Reset the guard when the logged-in user actually changes (login/logout)
  useEffect(() => {
    hasFetchedRef.current = false;
  }, [getStoredEmail()]);

  // Load data: fires on mount (email dep) AND when user object arrives (user dep).
  // Two effects share the same hasFetchedRef guard so only one ever runs.
  // This fixes: blank page / infinite skeleton when navigating from HomePage
  // because auth context resolves user AFTER ProfilePage mounts, so the first
  // effect sees user=null and returns early — the second effect catches it.
  useEffect(() => {
    if (!user) return;
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    loadUserData();
    fetchInboxCounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getStoredEmail()]);

  useEffect(() => {
    if (!user) return;
    if (hasFetchedRef.current) return; // already loaded by first effect
    hasFetchedRef.current = true;
    loadUserData();
    fetchInboxCounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // ── loadUserData ──────────────────────────────────────────────────────────
  // ✅ FIX 1: Uses ?seller=me to fetch only this user's ads — no more
  //    downloading all marketplace ads and filtering client-side.
  // ✅ FIX 2: Uses /api/ads/my-stats/ for stats — replaces N per-ad
  //    /rate/ round-trips that blocked the spinner.
  const loadUserData = async () => {
    try {
      setLoading(true);
      setUserProducts([]);
      const token = localStorage.getItem('authToken') || localStorage.getItem('token');
      if (!token) { setLoading(false); return; }

      // ── Fetch FIRST PAGE immediately — display data without waiting for all pages ──
      const firstRes = await fetch(`${API_BASE}/ads/?seller=me`, {
        headers: { Authorization: `Token ${token}` },
      });

      if (!firstRes.ok) {
        console.error('❌ Failed to load user ads:', firstRes.status);
        setLoading(false);
        return;
      }

      const firstData  = await firstRes.json();
      const firstPage  = Array.isArray(firstData) ? firstData : (firstData.results || firstData.ads || []);
      const nextUrl    = (!Array.isArray(firstData) && firstData.next) ? firstData.next : null;

      // ✅ Show first page immediately — page feels instant
      setUserProducts(firstPage);
      setLoading(false);

      // ── Stats — fire in parallel with remaining pages ──────────────────
      let allAds = [...firstPage];

      // Wrap in a HEAD-check pattern: try the endpoint but treat 404 as null
      // so no red network error appears in DevTools for a missing endpoint.
      const statsPromise = fetch(`${API_BASE}/ads/my-stats/`, {
        headers: { Authorization: `Token ${token}` },
      }).then(res => (res.ok ? res.json() : null)).catch(() => null);

      // ── Fetch remaining pages in background (non-blocking) ────────────
      if (nextUrl) {
        (async () => {
          let url = nextUrl;
          while (url) {
            try {
              const res = await fetch(url, { headers: { Authorization: `Token ${token}` } });
              if (!res.ok) break;
              const data = await res.json();
              const page = Array.isArray(data) ? data : (data.results || data.ads || []);
              allAds = allAds.concat(page);
              // Update UI progressively as each page arrives
              setUserProducts(prev => {
                const ids = new Set(prev.map(p => p.id));
                const newItems = page.filter(p => !ids.has(p.id));
                return newItems.length ? [...prev, ...newItems] : prev;
              });
              url = (!Array.isArray(data) && data.next) ? data.next : null;
            } catch (e) {
              console.warn('Background page fetch error:', e);
              break;
            }
          }
        })();
      }

      // ── Resolve stats ──────────────────────────────────────────────────
      let activeCount = firstPage.filter(ad => ad.status !== 'sold').length;
      let totalLikes  = 0;
      let followers   = 0;
      let rating      = 0;
      let reviewCount = 0;

      try {
        const s = await statsPromise;
        if (s && typeof s === 'object' && !s.error) {
          activeCount = s.active_listings ?? activeCount;
          totalLikes  = s.total_likes     ?? 0;
          followers   = s.followers       ?? 0;
          rating      = s.average_rating  ?? 0;
          reviewCount = s.review_count    ?? 0;
        } else {
          // Fallback: derive from first page data
          totalLikes = firstPage.reduce(
            (sum, ad) => sum + (ad.like_count || ad.likes_count || 0), 0
          );
          const userIdentifier = user?.identifier?.toLowerCase();
          if (userIdentifier) {
            const fRes = await fetch(
              `${API_BASE}/users/${encodeURIComponent(userIdentifier)}/follow/status/`,
              { headers: { Authorization: `Token ${token}` } }
            ).catch(() => null);
            if (fRes?.ok) {
              followers = (await fRes.json()).follower_count ?? 0;
            }
          }
        }
      } catch (e) {
        console.warn('Could not fetch seller stats:', e);
      }

      setStats({ activeListings: activeCount, totalLikes, followers, rating, reviewCount });
    } catch (error) {
      console.error('Error loading user data:', error);
      setLoading(false);
    }
  };

  // ── getMemberSinceDate ────────────────────────────────────────────────────
  // Tries several date fields on the user object before falling back to
  // a localStorage watermark (first time this user opened their profile).
  const getMemberSinceDate = () => {
    const fields = ['date_joined', 'created_at', 'createdAt', 'joinDate'];
    for (const f of fields) {
      if (user?.[f]) return new Date(user[f]);
    }
    const key    = `userSignupDate_${user?.identifier}`;
    const stored = localStorage.getItem(key);
    if (stored) return new Date(stored);
    const today = new Date();
    localStorage.setItem(key, today.toISOString());
    return today;
  };

  // ── Profile image handlers ────────────────────────────────────────────────
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setPreviewUrl(reader.result);
      reader.readAsDataURL(file);
    }
  };

  // ✅ FIX: handleUpload now POSTs to the backend via multipart FormData,
  // gets the real Supabase URL back, and updates the AuthContext + localStorage.
  // Previously it only saved the base64 preview locally — the Supabase URL
  // was never fetched back so the profile photo never updated on screen.
  // handleUpload is kept for API compatibility but SettingsTab.handleSave now
  // handles the full upload+display flow itself. This function is a no-op so it
  // doesn't fire a second PATCH that races back and wipes the freshly-set URL.
  const handleUpload = async () => {};

  const handleCancel = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
  };

  // ── Logout handler ────────────────────────────────────────────────────────
  const handleLogout = () => {
    if (window.confirm('Are you sure you want to logout?')) {
      logout();
      window.location.href = '/';
    }
  };

  // ── Shared props passed to all tab components ─────────────────────────────
  // Centralised here so each tab can access user data and trigger refreshes
  // without needing their own separate fetch logic.
  const sharedProps = {
    user,
    userProducts,
    loading,
    stats,
    setStats,
    setUserProducts,
    messages,
    notifications,
    updates,
    setMessages,
    setNotifications,
    setUpdates,
    onInboxRefresh: fetchInboxCounts,
  };

  // ── Tab badge total ───────────────────────────────────────────────────────
  // Drives the red dot on the Messages tab button
  const totalMsgBadge = unreadMsgCount + newLikesCount + newFollowersCount;

  // ── Tab definitions ───────────────────────────────────────────────────────💬, 🏪, 🎁, 👤.
  const tabs = [
    { id: 'messages',  icon: '', label: 'Messages',       badge: totalMsgBadge },
    { id: 'selling',   icon: '', label: 'Selling',        badge: 0 },
    { id: 'referrals', icon: '', label: 'Referrals',      badge: 0 },
    { id: 'settings',  icon: '', label: 'Profile Update', badge: 0 },
  ];

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className={styles.profilePageContainer}>
      <div className={styles.profileContentWrapper}>

        {/* ── Header with tab navigation ─────────────────────────────────── */}
        <div className={styles.profileHeaderSection}>
          <h1 className={styles.profileMainTitle}>My Profile</h1>

          <div className={styles.tabNavigationWrapper}>
            <div className={styles.tabNavigation}>
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  className={`${styles.tabButton} ${activeTab === tab.id ? styles.tabButtonActive : ''}`}
                  onClick={() => {
                    setActiveTab(tab.id);
                    // Sync active tab into URL so page can be bookmarked/shared
                    const url = new URL(window.location.href);
                    url.searchParams.set('profile_tab', tab.id);
                    window.history.replaceState({}, '', url.toString());
                  }}
                >
                  <span className={styles.tabIcon}>{tab.icon}</span>
                  <span className={styles.tabLabel}>{tab.label}</span>
                  {tab.badge > 0 && (
                    <span className={styles.tabBadgeDot}>
                      {tab.badge > 99 ? '99+' : tab.badge}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* ── Tab content ────────────────────────────────────────────────── */}
        <main className={styles.mainContentFullWidth}>
          {activeTab === 'messages'  && <MessagesTab  {...sharedProps} />}
          {activeTab === 'selling'   && <SellingTab   {...sharedProps} />}
          {activeTab === 'referrals' && <ReferralsTab {...sharedProps} />}
          {activeTab === 'settings'  && (
            <SettingsTab
              {...sharedProps}
              profileImage={profileImage}
              previewUrl={previewUrl}
              selectedFile={selectedFile}
              handleFileSelect={handleFileSelect}
              handleUpload={handleUpload}
              handleCancel={handleCancel}
              getMemberSinceDate={getMemberSinceDate}
              handleLogout={handleLogout}
              onProfileImageSaved={(newUrl) => {
                updateProfileImage(newUrl);
                setPreviewUrl(null);
                setSelectedFile(null);
              }}
            />
          )}
        </main>

      </div>
    </div>
  );
};

export default ProfilePage;

